import { initializeApp, getApps } from 'firebase/app';
import { getAuth, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import {
  getDatabase,
  ref,
  child,
  push,
  set,
  update,
  remove,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  onValue,
  onDisconnect,
  serverTimestamp,
  query,
  orderByChild,
  startAt,
  limitToLast,
  off
} from 'firebase/database';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const hasFirebaseConfig = Boolean(firebaseConfig.apiKey && firebaseConfig.databaseURL && firebaseConfig.projectId);
const app = hasFirebaseConfig ? (getApps()[0] || initializeApp(firebaseConfig)) : null;
const db = app ? getDatabase(app) : null;
const firebaseAuth = app ? getAuth(app) : null;

/**
 * The Realtime Database rules require an authenticated Firebase identity.
 * Application users live in PostgreSQL, not Firebase Auth, so the backend mints a
 * custom token (uid = PostgreSQL user id, claims = role/chatType) at login and on
 * every token refresh. Until this sign-in completes, every RTDB read and write is
 * denied by the rules, so all realtime access must wait on it.
 */
let firebaseSignInPromise = null;
let signedInUid = null;
let lastFirebaseToken = null;
let realtimeRetryTimer = null;
let realtimeRetryAttempt = 0;
let lastRealtimeErrorMessage = null;

const attachActiveRealtime = () => {
  if (!db || !isRealtimeAuthenticated()) return;

  activeRooms.forEach((roomId) => attachRoomListener(roomId));
  if (currentUserId) connectSocket(currentUserId);
};

const scheduleRealtimeRetry = () => {
  if (!lastFirebaseToken || realtimeRetryTimer || realtimeRetryAttempt >= 3) return;

  realtimeRetryAttempt += 1;
  const delay = Math.min(1000 * (2 ** (realtimeRetryAttempt - 1)), 8000);
  console.warn(`[realtime] retry scheduled in ${delay}ms (${realtimeRetryAttempt}/3)`);
  realtimeRetryTimer = setTimeout(() => {
    realtimeRetryTimer = null;
    authenticateRealtime(lastFirebaseToken).catch(() => {});
  }, delay);
};

export const authenticateRealtime = async (firebaseToken) => {
  if (!firebaseAuth || !firebaseToken) return false;
  lastFirebaseToken = firebaseToken;

  // Decode the expected UID from the custom token (it's a JWT: header.payload.sig).
  // We do a best-effort parse; if it fails we always re-authenticate.
  let expectedUid = null;
  try {
    const payload = JSON.parse(atob(firebaseToken.split('.')[1]));
    expectedUid = payload?.uid || null;
  } catch (_) {}

  // Already signed in as the correct user — nothing to do.
  if (
    expectedUid &&
    signedInUid === expectedUid &&
    firebaseAuth.currentUser?.uid === expectedUid
  ) {
    return true;
  }

  // Either no signed-in user, wrong user, or token expired — sign in fresh.
  if (!firebaseSignInPromise) {
    firebaseSignInPromise = signInWithCustomToken(firebaseAuth, firebaseToken)
      .then((credential) => {
        signedInUid = credential.user.uid;
        realtimeRetryAttempt = 0;
        lastRealtimeErrorMessage = null;
        console.info('[realtime] Firebase auth succeeded');
        // A room may have been joined while authentication was pending.
        attachActiveRealtime();
        return true;
      })
      .catch((error) => {
        // Surfaced deliberately: a silent failure here looks like "realtime is
        // just slow" while in reality every subscription is being denied.
        const message = error?.message || String(error);
        if (message !== lastRealtimeErrorMessage) {
          console.error('[realtime] Firebase sign-in failed; API chat remains available:', message);
          lastRealtimeErrorMessage = message;
        }
        signedInUid = null;
        scheduleRealtimeRetry();
        return false;
      })
      .finally(() => {
        firebaseSignInPromise = null;
      });
  }

  return firebaseSignInPromise;
};


export const isRealtimeAuthenticated = () => Boolean(signedInUid);

export const signOutRealtime = async () => {
  signedInUid = null;
  lastFirebaseToken = null;
  realtimeRetryAttempt = 0;
  if (realtimeRetryTimer) {
    clearTimeout(realtimeRetryTimer);
    realtimeRetryTimer = null;
  }
  activeRooms.clear();
  if (firebaseAuth) await firebaseAuth.signOut().catch(() => {});
};

if (firebaseAuth) {
  onAuthStateChanged(firebaseAuth, (fbUser) => {
    signedInUid = fbUser ? fbUser.uid : null;
    if (fbUser) attachActiveRealtime();
  });

  // Retry after a temporary network interruption without requiring a refresh.
  const retryWhenAvailable = () => {
    if (lastFirebaseToken && !isRealtimeAuthenticated()) {
      realtimeRetryAttempt = 0;
      scheduleRealtimeRetry();
    }
  };
  window.addEventListener('online', retryWhenAvailable);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') retryWhenAvailable();
  });
}

let currentUserId = null;
const activeRooms = new Set();
const roomListeners = new Map();
const typingListeners = new Map();
const callbackSets = {
  new_message: new Set(),
  message_edited: new Set(),
  customer_registered: new Set(),
  user_typing: new Set(),
  user_online: new Set(),
  user_offline: new Set(),
  messages_read: new Set(),
  messages_delivered: new Set(),
  incoming_call: new Set(),
  chat_moved: new Set(),
  webrtc_offer: new Set(),
  webrtc_answer: new Set(),
  webrtc_ice_candidate: new Set(),
  call_ended: new Set(),
  call_rejected: new Set(),
  call_accepted: new Set(),
  conference_user_joined: new Set(),
  conference_user_left: new Set()
};

function emitLocal(eventName, payload) {
  const callbacks = callbackSets[eventName];
  if (!callbacks) return;
  callbacks.forEach((callback) => callback(payload));
}

function addListener(eventName, callback) {
  if (!callbackSets[eventName]) callbackSets[eventName] = new Set();
  callbackSets[eventName].add(callback);
}

function removeListener(eventName, callback) {
  callbackSets[eventName]?.delete(callback);
}

function normalizeMessage(roomId, raw = {}) {
  return {
    ...raw,
    room_id: raw.room_id || raw.roomId || roomId,
    roomId: raw.roomId || raw.room_id || roomId,
    sender_id: raw.sender_id || raw.senderId,
    senderId: raw.senderId || raw.sender_id,
    sender_role: raw.sender_role || raw.senderRole,
    senderRole: raw.senderRole || raw.sender_role,
    image_url: raw.image_url || raw.imageUrl || null,
    imageUrl: raw.imageUrl || raw.image_url || null,
    voice_url: raw.voice_url || raw.voiceUrl || null,
    voiceUrl: raw.voiceUrl || raw.voice_url || null,
    createdAt: raw.createdAt || Date.now(),
    created_at: raw.created_at || new Date(raw.createdAt || Date.now()).toISOString()
  };
}

function attachRoomListener(roomId) {
  if (!db || roomListeners.has(roomId)) return;
  const joinTimestamp = Date.now() - 5000;
  const messagesRef = query(ref(db, `roomMessages/${roomId}`), orderByChild('createdAt'), startAt(joinTimestamp));
  const unsubscribeMessages = onChildAdded(messagesRef, (snapshot) => {
    const message = normalizeMessage(roomId, snapshot.val() || {});
    emitLocal('new_message', { room_id: roomId, roomId, message });
    const botType = message.bot_type || message.botType || message.media?.bot_type || message.media?.botType;
    if (botType === 'customer_registered') {
      const regData = message.bot_data || message.botData || message.media?.bot_data || message.media?.botData || {};
      emitLocal('customer_registered', {
        room_id: roomId,
        roomId,
        customer_id: regData.customerId || regData.customer_id,
        customerId: regData.customerId || regData.customer_id,
        name: regData.name || message.sender_name,
        phone: regData.phone,
        is_guest: false,
        registered_by: regData.registered_by,
        registered_by_id: regData.registered_by_id
      });
    }
  });

  const unsubscribeMessageEdits = onChildChanged(ref(db, `roomMessages/${roomId}`), (snapshot) => {
    const message = normalizeMessage(roomId, snapshot.val() || {});
    emitLocal('message_edited', { room_id: roomId, roomId, message });
    if (message.read || (message.readBy && Object.keys(message.readBy).length > 0) || (message.read_by && Object.keys(message.read_by).length > 0)) {
      emitLocal('messages_read', { room_id: roomId, roomId, user_id: message.sender_id || message.senderId, message_ids: [message.id] });
    }
  });

  const unsubscribeMessageRemoved = onChildRemoved(ref(db, `roomMessages/${roomId}`), (snapshot) => {
    const messageId = snapshot.key;
    emitLocal('message_deleted', { room_id: roomId, roomId, message_id: messageId, messageId });
  });

  const typingRef = ref(db, `roomTyping/${roomId}`);
  const unsubscribeTyping = onValue(typingRef, (snapshot) => {
    const typingUsers = snapshot.val() || {};
    const now = Date.now();
    Object.entries(typingUsers).forEach(([userId, value]) => {
      if (userId !== currentUserId) {
        const isFresh = value?.updatedAt ? (now - value.updatedAt < 5000) : true;
        const activeTyping = Boolean(value?.isTyping) && isFresh;
        emitLocal('user_typing', {
          room_id: roomId,
          roomId,
          user_id: userId,
          userId,
          is_typing: activeTyping,
          isTyping: activeTyping
        });
      }
    });
  });

  roomListeners.set(roomId, () => {
    unsubscribeMessages();
    unsubscribeMessageEdits();
    unsubscribeMessageRemoved();
    unsubscribeTyping();
  });
}

export const connectSocket = (userId) => {
  currentUserId = userId;
  if (!db || !userId || !isRealtimeAuthenticated()) return;
  const presenceRef = ref(db, `presence/${userId}`);
  set(presenceRef, { online: true, lastSeen: serverTimestamp() }).catch(() => {});
  onDisconnect(presenceRef).set({ online: false, lastSeen: serverTimestamp() }).catch(() => {});
};

export const disconnectSocket = () => {
  if (db && currentUserId && isRealtimeAuthenticated()) {
    set(ref(db, `presence/${currentUserId}`), { online: false, lastSeen: serverTimestamp() }).catch(() => {});
  }
  currentUserId = null;
  roomListeners.forEach((unsubscribe) => unsubscribe());
  roomListeners.clear();
  typingListeners.forEach((unsubscribe) => unsubscribe());
  typingListeners.clear();
};

export const joinRoom = async (roomId, userId) => {
  if (!roomId) return { ok: false, error: 'Room is required' };
  activeRooms.add(roomId);
  // RTDB requires Firebase custom-token authentication. Socket.IO/API
  // messaging remains available when Firebase is misconfigured or offline.
  if (isRealtimeAuthenticated()) attachRoomListener(roomId);
  // Room membership is NOT written here. /roomMembers is what the security rules
  // consult to authorize access, so it is owned exclusively by the backend
  // (Admin SDK) and denied to clients. A client writing its own membership would
  // simply be granting itself access to any room it names.
  return { ok: true };
};

export const leaveRoom = async (roomId, userId) => {
  activeRooms.delete(roomId);
  const unsubscribe = roomListeners.get(roomId);
  if (unsubscribe) unsubscribe();
  roomListeners.delete(roomId);
  return { ok: true };
};

export const sendMessage = async (roomId, message) => {
  if (!db || !isRealtimeAuthenticated()) return { ok: false, error: 'Firebase realtime is unavailable' };
  const messageRef = message?.id ? child(ref(db, `roomMessages/${roomId}`), message.id) : push(ref(db, `roomMessages/${roomId}`));
  const payload = normalizeMessage(roomId, {
    id: message?.id || messageRef.key,
    ...message,
    createdAt: message?.createdAt || Date.now(),
    updatedAt: Date.now()
  });
  await set(messageRef, payload);
  await update(ref(db, `chatRooms/${roomId}`), {
    lastMessage: payload.text || payload.document_name || payload.documentName || 'Media',
    lastMessageAt: payload.createdAt
  }).catch(() => {});
  return { ok: true, message: payload };
};

export const sendTypingIndicator = (roomId, userId, isTyping) => {
  if (!db || !isRealtimeAuthenticated() || !roomId || !userId) return;
  const typingRef = ref(db, `roomTyping/${roomId}/${userId}`);
  if (isTyping) {
    set(typingRef, { isTyping: true, updatedAt: serverTimestamp() }).catch(() => {});
    onDisconnect(typingRef).remove().catch(() => {});
  } else {
    remove(typingRef).catch(() => {});
  }
};

export const markMessagesRead = (roomId, messageIds, userId) => {
  if (!db || !isRealtimeAuthenticated() || !roomId || !userId || !Array.isArray(messageIds) || messageIds.length === 0) return;
  const updates = {};
  const validIds = [];
  messageIds.forEach((messageId) => {
    if (!messageId || messageId === 'undefined' || String(messageId).startsWith('temp_')) return;
    updates[`roomMessages/${roomId}/${messageId}/readBy/${userId}`] = true;
    updates[`roomMessages/${roomId}/${messageId}/read_by/${userId}`] = true;
    updates[`roomMessages/${roomId}/${messageId}/read`] = true;
    validIds.push(messageId);
  });
  if (Object.keys(updates).length > 0) {
    update(ref(db), updates).catch(() => {});
  }
  if (validIds.length > 0) {
    emitLocal('messages_read', { room_id: roomId, roomId, user_id: userId, message_ids: validIds });
  }
};

export const onNewMessage = (callback) => addListener('new_message', callback);
export const onMessageEdited = (callback) => addListener('message_edited', callback);
export const onMessageDeleted = (callback) => addListener('message_deleted', callback);
export const onCustomerRegistered = (callback) => addListener('customer_registered', callback);
export const onUserTyping = (callback) => addListener('user_typing', callback);
export const onUserOnline = (callback) => addListener('user_online', callback);
export const onUserOffline = (callback) => addListener('user_offline', callback);
export const onMessagesRead = (callback) => addListener('messages_read', callback);
export const onMessagesDelivered = (callback) => addListener('messages_delivered', callback);

export const offNewMessage = (callback) => removeListener('new_message', callback);
export const offMessageEdited = (callback) => removeListener('message_edited', callback);
export const offMessageDeleted = (callback) => removeListener('message_deleted', callback);
export const offCustomerRegistered = (callback) => removeListener('customer_registered', callback);
export const offUserTyping = (callback) => removeListener('user_typing', callback);
export const offUserOnline = (callback) => removeListener('user_online', callback);
export const offUserOffline = (callback) => removeListener('user_offline', callback);
export const offMessagesRead = (callback) => removeListener('messages_read', callback);
export const offMessagesDelivered = (callback) => removeListener('messages_delivered', callback);

const firebaseRealtimeBus = {
  connected: Boolean(db),
  id: 'firebase-rtdb',
  connect: () => connectSocket(currentUserId),
  disconnect: disconnectSocket,
  on: addListener,
  off: removeListener,
  emit: (eventName, payload, ack) => {
    if (eventName === 'typing') sendTypingIndicator(payload?.room_id || payload?.roomId, payload?.user_id || payload?.userId, payload?.is_typing ?? payload?.isTyping);
    if (eventName === 'join_room') joinRoom(payload?.room_id || payload?.roomId, payload?.user_id || payload?.userId).then((result) => ack?.(result));
    if (eventName === 'leave_room') leaveRoom(payload?.room_id || payload?.roomId, payload?.user_id || payload?.userId).then((result) => ack?.(result));
    if (eventName === 'send_message') sendMessage(payload?.room_id || payload?.roomId, payload?.message).then((result) => ack?.(result));
  }
};

export default firebaseRealtimeBus;
